import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';

// POI Icons mapping
const POI_ICONS = {
  stage: '🎭',
  food_stall: '🍔',
  restroom: '🚻',
  information: 'ℹ️',
  emergency: '🚑',
  ticket_booth: '🎫',
  entry_point: '🚪',
  exit_point: '🚪',
  parking: '🅿️',
  transportation: '🚌',
  beer_tent: '🍺',
  other: '📍'
};

// Create SVG icon for POI
const createPOIIcon = (category, isSelected = false) => {
  const emoji = POI_ICONS[category] || POI_ICONS.other;
  const strokeColor = isSelected ? '#e74c3c' : '#333';
  const strokeWidth = isSelected ? '4' : '2';
  return `
    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="14" fill="white" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>
      <text x="16" y="22" text-anchor="middle" font-size="16">${emoji}</text>
    </svg>
  `;
};

const GoogleMap = ({
  onLocationSelect,
  initialLat = null,
  initialLng = null,
  initialZoom = 15,
  height = '400px',
  pois = [],
  onPOIDrop,
  onPOISelect,
  selectedPOI,
  showSearch = true,
  readOnly = false
}) => {
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [map, setMap] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [marker] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const poiMarkersRef = useRef([]);

  // Stable callback to update location
  const updateLocation = useCallback((lat, lng, zoom) => {
    if (onLocationSelect) {
      onLocationSelect(lat, lng, zoom);
    }
  }, [onLocationSelect]);

  // Update marker and location based on map center
  const updateLocationFromCenter = useCallback(() => {
      console.log('updateLocationFromCenter');
      const currentMap = mapInstanceRef.current;
    if (currentMap) {
      const center = currentMap.getCenter();
      const zoom = currentMap.getZoom();
      const lat = center.lat();
      const lng = center.lng();

      console.log('Updating location from center:', { lat, lng, zoom });

      // Update or create marker at center
/* Disable markers for now. This will only confuse us later
   when we add use markers for POIs
    if (marker) {
        marker.setPosition({ lat, lng });
      } else {
        const newMarker = new window.google.maps.Marker({
          position: { lat, lng },
          map: currentMap,
          draggable: true,
          title: 'Festival Location'
        });

        // Add drag listener to marker
        newMarker.addListener('dragend', (event) => {
          const newLat = event.latLng.lat();
          const newLng = event.latLng.lng();
          updateLocation(newLat, newLng, map.getZoom());
        });

        setMarker(newMarker);
      }*/

      updateLocation(lat, lng, zoom);
    }
  }, [updateLocation]); // marker removed - not currently used

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
        },
        (error) => {
          console.warn('Could not get user location:', error);
          // Default to a central location (e.g., Stockholm, Sweden)
          setUserLocation({ lat: 59.3293, lng: 18.0686 });
        }
      );
    } else {
      // Default location if geolocation is not supported
      setUserLocation({ lat: 59.3293, lng: 18.0686 });
    }
  }, []);

  // Initialize map when component mounts and user location is available
  useEffect(() => {
    const waitForGoogleMaps = () => {
      return new Promise((resolve) => {
        if (window.google && window.google.maps) {
          resolve();
        } else {
          const checkGoogle = () => {
            if (window.google && window.google.maps) {
              resolve();
            } else {
              setTimeout(checkGoogle, 100);
            }
          };
          checkGoogle();
        }
      });
    };

    const initializeMap = async () => {
      if (mapRef.current && userLocation && !map) {
        try {
          // Wait for Google Maps API to load
          await waitForGoogleMaps();

          const center = initialLat && initialLng
            ? { lat: parseFloat(initialLat), lng: parseFloat(initialLng) }
            : userLocation;

          console.log('Initializing map with center:', center);

          const mapOptions = {
            center: center,
            zoom: initialZoom,
            mapTypeControl: !readOnly,
            streetViewControl: !readOnly,
            fullscreenControl: !readOnly,
            zoomControl: !readOnly,
            // Disable all interactions when readOnly
            draggable: !readOnly,
            scrollwheel: !readOnly,
            disableDoubleClickZoom: readOnly,
            gestureHandling: readOnly ? 'none' : 'auto'
          };

          const newMap = new window.google.maps.Map(mapRef.current, mapOptions);

          setMap(newMap);
          mapInstanceRef.current = newMap;

          // Also set initial coordinates if provided
          if (initialLat && initialLng) {
            updateLocation(parseFloat(initialLat), parseFloat(initialLng), initialZoom);
          }          

        } catch (error) {
          console.error('Failed to initialize Google Maps:', error);
        }
      }
    };

    initializeMap();
  }, [userLocation, map, initialLat, initialLng, initialZoom, updateLocationFromCenter, updateLocation, readOnly]);

// This new useEffect handles listeners and will trigger once the 'map' state is set.
useEffect(() => {
  if (map) {
    // Correctly debounce the function call to prevent too many updates
    let timeoutId = null;

    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        updateLocationFromCenter();
      }, 500);
    };

    const centerListener = map.addListener('center_changed', debouncedUpdate);
    const zoomListener = map.addListener('zoom_changed', debouncedUpdate);

    // Clean up listeners when the component unmounts or map changes
    return () => {
      window.google.maps.event.removeListener(centerListener);
      window.google.maps.event.removeListener(zoomListener);
      clearTimeout(timeoutId);
    };
  }
}, [map, updateLocationFromCenter]);

  // Render POI markers
  useEffect(() => {
    if (!map || !pois || pois.length === 0) {
      return;
    }

    // Clear existing POI markers
    poiMarkersRef.current.forEach(marker => marker.setMap(null));
    poiMarkersRef.current = [];

    // Create new POI markers
    const newMarkers = pois.map(poi => {

      // Convert string coordinates to numbers
      const lat = parseFloat(poi.latitude);
      const lng = parseFloat(poi.longitude);

      if (isNaN(lat) || isNaN(lng)) {
        return null;
      }

      const isSelected = selectedPOI?.id === poi.id;
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map: map,
        title: poi.name,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(createPOIIcon(poi.category, isSelected))}`,
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 32)
        },
        draggable: true
      });

      // Add click listener for POI selection
      marker.addListener('click', () => {
        if (onPOISelect) {
          onPOISelect(poi);
        }
      });

      // Add drag listener for POI movement
      marker.addListener('dragend', (event) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();

        // Update POI position - this should update the existing POI, not create a new one
        if (onPOIDrop) {
          onPOIDrop(newLat, newLng, poi.category, poi.id);
        }
      });

      return marker;
    }).filter(marker => marker !== null); // Filter out null markers

    poiMarkersRef.current = newMarkers;

    return () => {
      newMarkers.forEach(marker => marker.setMap(null));
    };
  }, [map, pois, onPOISelect, onPOIDrop, selectedPOI]);

  // Handle drag and drop from POI palette
  useEffect(() => {
    if (!map) return;

    const handleDrop = (event) => {
      event.preventDefault();
      const dropData = event.dataTransfer.getData('text/plain');

      if (dropData && onPOIDrop) {
        const rect = mapRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Convert pixel coordinates to lat/lng
        const bounds = map.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        const lat = sw.lat() + (ne.lat() - sw.lat()) * (1 - y / rect.height);
        const lng = sw.lng() + (ne.lng() - sw.lng()) * (x / rect.width);

        // Try to parse as JSON (for unplaced POIs), otherwise treat as simple POI type
        let poiTypeOrData;
        try {
          poiTypeOrData = JSON.parse(dropData);
        } catch (e) {
          // Simple POI type string
          poiTypeOrData = dropData;
        }

        onPOIDrop(lat, lng, poiTypeOrData);
      }
    };

    const handleDragOver = (event) => {
      event.preventDefault();
    };

    const mapElement = mapRef.current;
    if (mapElement) {
      mapElement.addEventListener('drop', handleDrop);
      mapElement.addEventListener('dragover', handleDragOver);

      return () => {
        mapElement.removeEventListener('drop', handleDrop);
        mapElement.removeEventListener('dragover', handleDragOver);
      };
    }
  }, [map, onPOIDrop]);

  // Note: Using simple geocoding instead of Places API due to deprecation

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    if (!searchValue.trim() || !map) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchValue }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        // Move map to searched location
        map.setCenter({ lat, lng });
        map.setZoom(12);

        // Clear search input
        setSearchValue('');
      } else {
        console.warn('Geocoding failed:', status);
      }
    });
  };

  return (
    <div style={{ position: 'relative', height: height }}>


      {/* Search field - only show when showSearch is true */}
      {showSearch && (
        <div style={{ marginBottom: '10px', display: 'flex', gap: '8px' }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search for a city or location..."
            value={searchValue}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyPress}
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <button
            type="button"
            onClick={handleSearch}
            style={{
              padding: '12px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Search
          </button>
        </div>
      )}

      {/* Map container */}
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: height,
          borderRadius: showSearch ? '8px' : '0px',
          border: showSearch ? '1px solid #ddd' : 'none',
          background: '#f0f0f0'
        }}
      />
    </div>
  );
};

const GoogleMapWrapper = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <div style={{
        height: props.height || '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f0f0',
        border: '1px solid #ddd',
        borderRadius: '8px',
        color: '#666'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h4>Google Maps API Key Required</h4>
          <p>Add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file</p>
        </div>
      </div>
    );
  }

  return (
    <Wrapper apiKey={apiKey}>
      <GoogleMap {...props} />
    </Wrapper>
  );
};

export default GoogleMapWrapper;
